import { workspaceAvatarStyle } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { SYMPHONY_GREEN } from '../../SymphonyMark';
import { isAccountRoot } from '../../../lib/file-browser';

/**
 * THE folder mark — a two-tone filled folder, drawn in one of TWO palettes depending on whose folder it is.
 *
 * ## A Space's folder wears the Space
 *
 *   folder BODY → `style.background`   (the avatar tile's fill)
 *   folder TAB  → `style.initials`     (the ink the avatar writes its letters in)
 *
 * `workspaceAvatarStyle` is common-lib's 5-style, id-hashed rule — the same one `WorkspaceBadge` draws with
 * and the same one both phones mirror — so the folder for a given Space is byte-identical on every surface.
 * A Space with an UPLOADED icon still shows that icon in its avatar; the folder glyph always uses the style
 * colours, because there is no icon-derived hue to take.
 *
 * ## An ACCOUNT ROOT's folder wears Symphony
 *
 * `skills`, `brain` and `knowledge` are not Spaces — they are Symphony's own three directories (see
 * `ACCOUNT_ROOTS`), and hashing their literal names through the Space palette gave them three unrelated
 * Space hues, which said "these are Spaces you have forgotten the name of". They are drawn in the BRAND
 * pair instead — `SYMPHONY_GREEN` (the lime of the Symphony app tile, imported from the mark itself so the
 * two cannot drift) under the brand's own mid-green tab. The COLOUR is the whole distinction: a Space is
 * one of five hashed hues, Symphony's own three are the brand, and that is enough to tell the two kinds of
 * row apart.
 *
 * The three shipped with a per-directory glyph on the body as well — a sparkle for Skills, a robot for
 * Agents, a brain for Memory — and it is gone by product decision: the folder is a colour, not a colour and
 * a pictogram. There is nothing to put back on the body, so the account-root branch is now a two-line
 * decision about fills, exactly like a Space's.
 *
 * Both brand values are mode-independent, which is the property the drawing rests on: the lime does not move
 * between light and dark, so neither does the tab laid beside it.
 *
 * ONE vector, whatever the palette: the tones are values, so a new style costs nothing here. The colours
 * arrive as attribute values computed in TS — the same escape hatch `WorkspaceBadge` uses for
 * `background`/`borderColor`/`color`, which is what keeps `test/design-tokens.test.ts` happy (there is no
 * literal in this file, and no `--ds-*`/`--sym-*` token could carry a per-Space hue).
 *
 * Geometry is the Figma glyph's own box, 75 × 59 (≈1.27:1), and the two folder paths below are the exported
 * vector VERBATIM (Figma `Group 2147239767` → `Fill 6` tab / `Fill 2` body) rather than a redrawn
 * approximation: the tab's trailing edge is a diagonal, and its leading corner is rounded to 3.05.
 *
 * ⚠️ **The BODY is painted FIRST and the TAB over it — the order is part of the drawing, not a detail.**
 * The body's top edge is not straight in the vector: it spikes UP to y 6.104 at x 22.07, four units above the
 * y 10.1729 it runs at on either side. That spike is an OVERLAP, there so no antialiasing seam can open where
 * the two fills meet, and it is meant to be painted over. Paint the body last and the spike is what you see —
 * the folder's top edge grows a triangle in the body's colour, which is the bug design reported in Sept 2026
 * (“the top edge of the rectangle is getting a triangular shape instead of staying straight”). The tab covers
 * x 0–44.11 across the whole of y 0–10.17, so with the tab in front the spike is hidden and the edge reads
 * straight. `test/files-redesign.test.ts` pins the order for that reason.
 *
 * Mirrored 1:1 by iOS `FolderGlyph.swift` and Android `FolderGlyph.kt` — same two palettes, same coordinates,
 * same paint order.
 */

/**
 * ⚠️ The TAB is NOT the ink-on-lime token, and re-pointing it there is a regression rather than a tidy-up.
 *
 * The tab sticks out ABOVE the body, so it is drawn on the PAGE — the one thing in this drawing that DOES
 * change between light and dark. The DS's ink-on-lime role is `#14250B` against a `#1F1F1F` page,
 * near-identical, so a tab painted in it vanished and the folder read as a lime rectangle with a bite out
 * of its corner.
 * (On the `#333232` card it was merely marginal, which is worse — it looks fine until you open the one screen
 * that puts a folder on the page.)
 *
 * The tab is the brand's own mid-green instead: legible on white, on the light page, on the dark card AND on
 * the dark page, while still reading as the darker half of a two-tone brand folder. It is mode-independent,
 * like the lime beside it, so the folder is one drawing in both themes — only the ground beneath it moves.
 */
const SYMPHONY_TAB = 'var(--ds-fill-accent-brand-hover)';

export function FolderGlyph({ workspaceId, width = 18 }: { workspaceId?: string; width?: number }) {
  const id = workspaceId ?? '';
  // One decision, read twice: an account root wears the brand pair, everything else wears its Space's.
  // Keyed off `isAccountRoot` rather than a local list, so a fourth directory cannot quietly acquire the
  // brand pair — or quietly miss it.
  const accountRoot = isAccountRoot(id);
  const style = workspaceAvatarStyle(id);
  const bodyFill = accountRoot ? SYMPHONY_GREEN : style.background;
  const tabFill = accountRoot ? SYMPHONY_TAB : style.initials;
  return (
    <svg
      aria-hidden
      width={width}
      height={(width * 59) / 75}
      viewBox="0 0 75 59"
      fill="none"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* The BODY first — its top edge carries the overlap spike that the tab is drawn over. */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M71.6916 10.1729L44.1218 10.1729L22.0659 6.104L0.0100212 10.1729L0.0100212 55.9483C0.0100212 57.6348 1.49218 59 3.3184 59H71.6916C73.5178 59 75 57.6348 75 55.9483V13.2246C75 11.538 73.5178 10.1729 71.6916 10.1729Z"
        fill={bodyFill}
      />
      {/* …and the TAB over it: it is the folded flap in FRONT, and it is what hides the spike. */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M44.1118 10.1723L22.0559 10.1723H0L0 3.05169C0 1.36512 1.48216 0 3.30838 0L31.4759 0C33.011 0 34.4777 0.589994 35.5188 1.62757L44.1118 10.1723Z"
        fill={tabFill}
      />
    </svg>
  );
}
